<template>
    <div v-if="page && (node || wire)" class="space-y-2 p-1 text-sm text-white">
        <header class="flex items-center justify-between gap-2 rounded px-2 py-1" :style="{ background: view.color }">
            <span class="truncate font-semibold">{{ view.icon }} {{ view.title }}</span>
            <form-button color="danger" width="auto" height="24px"
                :title="node ? 'Delete this node and its wires' : 'Delete this wire'"
                @click="logic.deleteSelection(page)">Delete</form-button>
        </header>
        <ul v-if="diagnostics.length" class="space-y-0.5">
            <li v-for="item in diagnostics" :key="item.code" class="text-xs italic"
                :class="item.severity === 'error' ? 'text-red-300' : 'text-amber-300'">{{ item.message }}</li>
        </ul>

        <EventNodeProperties v-if="node?.kind === 'event'" :page="page" :graph="graph" :node="node" :context="context" />
        <GpioNodeProperties v-else-if="node?.kind === 'gpio'" :page="page" :graph="graph" :node="node"
            :entities="gpioEntities" :plate-name="settings?.plate?.name ?? ''" />
        <LogicBindingProperties v-else-if="node?.kind === 'binding'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" />
        <LogicConditionProperties v-else-if="node?.kind === 'condition'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" />
        <LogicSwitchProperties v-else-if="node?.kind === 'switch'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" />
        <LogicActionProperties v-else-if="node?.kind === 'action'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" :settings="settings" />
        <section v-else-if="wire" class="space-y-1 text-xs text-gray-200">
            <p><span class="text-gray-400">From</span> {{ endpointLabel(wire.from) }}</p>
            <p><span class="text-gray-400">To</span> {{ endpointLabel(wire.to) }}</p>
        </section>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import { useUndoStore } from '../../history/undoStore.js';
import { findNode, findWire, validateLogicGraph } from '../../logic/logicGraph.js';
import { createPageLogicContext } from '../../logic/pageLogic.js';
import { describeLogicNode } from '../../logic/logicPresentation.js';
import EventNodeProperties from './EventNodeProperties.vue';
import LogicActionProperties from './LogicActionProperties.vue';
import LogicBindingProperties from './LogicBindingProperties.vue';
import LogicConditionProperties from './LogicConditionProperties.vue';
import LogicSwitchProperties from './LogicSwitchProperties.vue';
import GpioNodeProperties from './GpioNodeProperties.vue';
import { useActionEditorContext } from '../properties/actions/useActionEditorContext.js';

const props = defineProps({
    layer: { type: Object, default: null },
    settings: { type: Object, default: () => ({}) },
});
const logic = useLogicStore();
const undo = useUndoStore();

// page.logic is plain data outside Vue; these counters say when it changed.
// The page is unwrapped so the graph handed to children stays plain data.
const revision = computed(() => `${logic.revision}|${undo.historyVersion}`);
const page = computed(() => {
    revision.value;
    const selection = logic.selection;
    return selection ? toRaw(props.layer)?.findOne?.('#' + selection.pageUuid) ?? null : null;
});
const graph = computed(() => {
    revision.value;
    return page.value?.logic ?? null;
});
// GPIO entity metadata of the selected plate: event choices of GPIO nodes depend on the entity's domain.
const { gpioMetadata } = useActionEditorContext(() => null, () => props.settings);
const gpioEntities = computed(() => gpioMetadata.value?.entities ?? []);
const context = computed(() => ({
    getObject: page.value ? createPageLogicContext(page.value).getObject : () => null,
    getGpioEntity: id => gpioEntities.value.find(entity => entity.id === id) ?? null,
}));
const node = computed(() => logic.selection?.kind === 'node' && graph.value ? findNode(graph.value, logic.selection.id) : null);
const wire = computed(() => logic.selection?.kind === 'wire' && graph.value ? findWire(graph.value, logic.selection.id) : null);
const view = computed(() => node.value
    ? describeLogicNode(node.value)
    : { icon: '〰', title: 'Wire', color: '#475569' });
const diagnostics = computed(() => (node.value && graph.value
    ? validateLogicGraph(graph.value, context.value).filter(item => item.nodeId === node.value.id)
    : []));

function endpointLabel(endpoint) {
    if (endpoint.object !== undefined) {
        const object = context.value.getObject(endpoint.object);
        return object ? `${object.obj} p${page.value.haspid}b${object.haspid}` : 'missing object';
    }
    const target = findNode(graph.value, endpoint.node);
    return target ? `${describeLogicNode(target).title} (${endpoint.port})` : 'missing node';
}
</script>
